

// src/pages/Leads/LeadList.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  Cog6ToothIcon,
  PencilSquareIcon,
  TrashIcon,
  CalendarIcon,
  ChatBubbleLeftIcon,
  XMarkIcon,
  ClockIcon,UserCircleIcon,
} from "@heroicons/react/24/outline";
import EditLeadDrawer from "../../components/Task/EditLeadDrawer";
export default function LeadList() {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);
  const navigate = useNavigate();

  const [openActionsMenu, setOpenActionsMenu] = useState(null);
  const [expandedLeads, setExpandedLeads] = useState(new Set());
  const [activities, setActivities] = useState({});

  // ─── Modal States ────────────────────────────────────────────────
  const [showLogCallModal, setShowLogCallModal] = useState(false);
  const [showSiteVisitModal, setShowSiteVisitModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showFollowupsModal, setShowFollowupsModal] = useState(false); // ← NEW
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Form states
  const [callForm, setCallForm] = useState({
    status: "Connected",
    notes: "",
    next_followup: "",
  });

  const [siteVisitForm, setSiteVisitForm] = useState({
    scheduled_date: "",
    notes: "",
  });

  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    due_date: "",
  });

  const [followupsData, setFollowupsData] = useState([]);
  const [siteVisitsData, setSiteVisitsData] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
const [editLeadId, setEditLeadId] = useState(null);
  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads", {
        params: { q: search, pipeline_stage: "Lead" },
      });
      setLeads(res.data.leads || res.data || []);
    } catch (err) {
      console.error("Error fetching leads:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(fetchLeads, 400);
    return () => clearTimeout(timer.current);
  }, [search]);

  useEffect(() => {
    for (let id of expandedLeads) {
      if (!activities[id]) {
        (async () => {
          try {
            const [callsRes, followRes] = await Promise.all([
              api.get(`/leads/${id}/calls`),
              api.get(`/leads/${id}/followups`),
            ]);
            const allActs = [
              ...callsRes.data.calls.map((c) => ({
                date: c.created_at || c.call_time,
                note: c.notes || `Call status: ${c.status}`,
                type: "call",
              })),
              ...followRes.data.followups.map((f) => ({
                date: f.scheduled_at,
                note: f.notes || "Follow-up scheduled",
                type: "followup",
              })),
            ].sort((a, b) => new Date(b.date) - new Date(a.date));
            setActivities((prev) => ({ ...prev, [id]: allActs }));
          } catch (err) {
            console.error("Failed to load activities for lead", id);
          }
        })();
      }
    }
  }, [expandedLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}/status`, { status: newStatus });
      fetchLeads();
    } catch (err) {
      alert("Failed to update status");
    }
    setOpenActionsMenu(null);
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await api.delete(`/leads/${leadId}`);
      fetchLeads();
    } catch (err) {
      alert("Failed to delete lead");
    }
  };

  const toggleExpand = (leadId, e) => {
    if (e.target.closest(".actions")) return;

    const newSet = new Set(expandedLeads);
    if (newSet.has(leadId)) newSet.delete(leadId);
    else newSet.add(leadId);
    setExpandedLeads(newSet);
  };

  // ─── Modal Open Handlers ──────────────────────────────────────────
  const openModal = async (modalType, leadId) => {
    setSelectedLeadId(leadId);
    setOpenActionsMenu(null);

    if (modalType === "logCall") {
      setCallForm({ status: "Connected", notes: "", next_followup: "" });
      setShowLogCallModal(true);
    } else if (modalType === "siteVisit") {
      setSiteVisitForm({ scheduled_date: "", notes: "" });
      setShowSiteVisitModal(true);
    } else if (modalType === "createTask") {
      setTaskForm({ title: "", description: "", due_date: "" });
      setShowCreateTaskModal(true);
    } else if (modalType === "viewFollowups") {
      setFollowupsLoading(true);
      setShowFollowupsModal(true);
      try {
        const [callsRes, followRes, siteRes] = await Promise.all([
          api.get(`/leads/${leadId}/calls`),
          api.get(`/leads/${leadId}/followups`),
          api.get(`/leads/${leadId}/pipeline`), // assuming visits are in pipeline
        ]);
        setFollowupsData(followRes.data.followups || []);
        setSiteVisitsData(siteRes.data.visits || []);
      } catch (err) {
        console.error("Failed to load followups/visits:", err);
      } finally {
        setFollowupsLoading(false);
      }
    }
  };

  // ─── Close Handlers ───────────────────────────────────────────────
  const closeLogCallModal = () => setShowLogCallModal(false);
  const closeSiteVisitModal = () => setShowSiteVisitModal(false);
  const closeCreateTaskModal = () => setShowCreateTaskModal(false);
  const closeFollowupsModal = () => setShowFollowupsModal(false);

  // ─── Submit Handlers ──────────────────────────────────────────────
  const handleCallSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLeadId) return;
    try {
      await api.post("/leads/call", {
        lead_id: selectedLeadId,
        status: callForm.status,
        notes: callForm.notes,
        next_followup: callForm.next_followup,
      });
      alert("Call logged successfully!");
      closeLogCallModal();
      fetchLeads();
    } catch (err) {
      alert("Failed to log call");
    }
  };

  const handleSiteVisitSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLeadId) return;
    try {
      await api.post("/leads/site-visit", {
        lead_id: selectedLeadId,
        scheduled_date: siteVisitForm.scheduled_date,
        notes: siteVisitForm.notes,
      });
      alert("Site visit scheduled successfully!");
      closeSiteVisitModal();
      fetchLeads();
    } catch (err) {
      alert("Failed to schedule site visit");
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLeadId) return;
    try {
      await api.post("/tasks", {
        lead_id: selectedLeadId,
        title: taskForm.title,
        description: taskForm.description,
        due_date: taskForm.due_date,
        module: "Lead",
      });
      alert("Task created successfully!");
      closeCreateTaskModal();
      fetchLeads();
    } catch (err) {
      alert("Failed to create task");
    }
  };
useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionsMenu !== null && !event.target.closest(".actions")) {
        setOpenActionsMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionsMenu]);
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 md:p-6">
      {/* Top Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-5 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-50"></h1>

        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-3">
          <div className="relative flex-1 min-w-[280px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500/60 transition-all"
            />
          </div>

          <button
            onClick={() => navigate("/leads/create")}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-medium rounded-xl shadow-md transition-all"
          >
            <PlusIcon className="w-5 h-5" />
            New Lead
          </button>
        </div>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="flex justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-orange-600"></div>
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20 text-gray-500 dark:text-gray-400">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
            <UserCircleIcon className="w-12 h-12 text-gray-400 dark:text-gray-600" />
          </div>
          <p className="text-xl font-medium">No leads found</p>
        </div>
      ) : (
        // <div className="space-y-4">
        // <div className="space-y-4 max-w-4xl mx-auto">
        <div className="space-y-4 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title – moved inside with padding */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Leads</h2>
          {leads.map((lead) => (
            <div
              key={lead.id}
              className={`
                relative z-10 group bg-white dark:bg-gray-800 
                border border-gray-200 dark:border-gray-700 
                rounded-xl overflow-visible           
                hover:border-orange-500 transition-all 
                cursor-pointer
                ${openActionsMenu === lead.id ? 'z-30 shadow-2xl' : 'z-10'}
              `}
              onClick={(e) => toggleExpand(lead.id, e)}
            >
              {/* Lead Card Content */}
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <ChevronRightIcon
                    className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${
                      expandedLeads.has(lead.id) ? "rotate-90" : ""
                    }`}
                  />

                  <div className="w-11 h-11 rounded-xl bg-orange-100 dark:bg-orange-950/60 flex items-center justify-center text-orange-600 dark:text-orange-400 font-semibold text-xl shrink-0 shadow-sm">
                    {lead.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg leading-tight truncate">
                      {lead.name || "Unnamed Lead"}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-0.5 mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                      {lead.mobile && (
                        <div className="flex items-center gap-1.5">
                          <PhoneIcon className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
                          <a href={`tel:${lead.mobile}`} onClick={(e) => e.stopPropagation()} className="hover:text-orange-600 dark:hover:text-orange-400 underline underline-offset-2 transition-colors truncate max-w-[140px]">
                            {lead.mobile}
                          </a>
                        </div>
                      )}
                      {lead.email && (
                        <div className="flex items-center gap-1.5">
                          <EnvelopeIcon className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
                          <a href={`mailto:${lead.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-orange-600 dark:hover:text-orange-400 underline underline-offset-2 transition-colors truncate max-w-[180px]">
                            {lead.email}
                          </a>
                        </div>
                      )}
                      {!lead.mobile && !lead.email && (
                        <span className="text-gray-500 dark:text-gray-400 text-xs italic">No contact info</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="relative z-20 flex items-center gap-1.5 sm:gap-2 actions">
                  {/* {lead.mobile && (
                    <a href={`tel:${lead.mobile}`} onClick={(e) => e.stopPropagation()} className="p-2.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-950/40 text-gray-600 dark:text-gray-400 hover:text-orange-600 transition-colors" title="Call">
                      <PhoneIcon className="w-5 h-5" />
                    </a>
                  )}

                  {lead.email && (
                    <a href={`mailto:${lead.email}`} onClick={(e) => e.stopPropagation()} className="p-2.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-950/40 text-gray-600 dark:text-gray-400 hover:text-orange-600 transition-colors" title="Email">
                      <EnvelopeIcon className="w-5 h-5" />
                    </a>
                  )} */}

                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionsMenu(openActionsMenu === lead.id ? null : lead.id);
                      }}
                      className={`
                        p-2.5 rounded-lg transition-colors
                        ${openActionsMenu === lead.id ? "bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-300" : "hover:bg-orange-50 dark:hover:bg-orange-950/40 text-gray-600 dark:text-gray-400 hover:text-orange-600"}
                      `}
                      title="Actions"
                    >
                      <Cog6ToothIcon className="w-5 h-5" />
                    </button>

                    {openActionsMenu === lead.id && (
                      <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl shadow-black/20 dark:shadow-black/40 overflow-hidden z-[1000] divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                        {/* <div className="py-1">
                          {["New Lead", "Follow Up", "Converted", "Stale"].map((status) => (
                            <button
                              key={status}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(lead.id, status);
                              }}
                              className="block w-full text-left px-5 py-3 hover:bg-orange-50 dark:hover:bg-orange-950/50 text-gray-800 dark:text-gray-200 hover:text-orange-700 dark:hover:text-orange-300 transition"
                            >
                              {status}
                            </button>
                          ))}
                        </div> */}

                        <div className="py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal("siteVisit", lead.id);
                            }}
                            className="flex w-full items-center gap-3 px-5 py-3 hover:bg-orange-50 dark:hover:bg-orange-950/50 text-gray-800 dark:text-gray-200 transition"
                          >
                            <CalendarIcon className="w-5 h-5 text-orange-500" />
                            Schedule Meeting
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal("createTask", lead.id);
                            }}
                            className="flex w-full items-center gap-3 px-5 py-3 hover:bg-orange-50 dark:hover:bg-orange-950/50 text-gray-800 dark:text-gray-200 transition"
                          >
                            <PlusIcon className="w-5 h-5 text-orange-500" />
                            Create Task
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal("logCall", lead.id);
                            }}
                            className="flex w-full items-center gap-3 px-5 py-3 hover:bg-orange-50 dark:hover:bg-orange-950/50 text-gray-800 dark:text-gray-200 transition"
                          >
                            <PhoneIcon className="w-5 h-5 text-orange-500" />
                            Log Call / Upload File
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal("viewFollowups", lead.id); // ← NEW: open follow-ups modal
                            }}
                            className="flex w-full items-center gap-3 px-5 py-3 hover:bg-orange-50 dark:hover:bg-orange-950/50 text-gray-800 dark:text-gray-200 transition"
                          >
                            <ChatBubbleLeftIcon className="w-5 h-5 text-orange-500" />
                            View Follow-ups & Meeting
                          </button>
                        </div>

                        {/* <div className="py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/leads/${lead.id}`);
                            }}
                            className="flex w-full items-center gap-3 px-5 py-3 text-gray-800 dark:text-gray-200 hover:bg-orange-50 dark:hover:bg-orange-950/50 transition"
                          >
                            <ChevronRightIcon className="w-5 h-5 text-orange-500" />
                            Open Full Lead Details
                          </button>
                        </div> */}
                      </div>
                    )}
                  </div>

                  {/* <button
                    onClick={(e) => {
                      e.stopPropagation();
                      alert("Edit coming soon");
                    }}
                    className="p-2.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-950/40 text-gray-600 dark:text-gray-400 hover:text-orange-600 transition-colors"
                    title="Edit"
                  >
                    <PencilSquareIcon className="w-5 h-5" />
                  </button> */}
                  <button
  onClick={(e) => {
    e.stopPropagation();
    setEditLeadId(lead.id);           // ← new state
  }}
  className="p-2.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-950/40 text-gray-600 dark:text-gray-400 hover:text-orange-600 transition-colors"
  title="Edit Lead"
>
  <PencilSquareIcon className="w-5 h-5" />
</button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteLead(lead.id);
                    }}
                    className="p-2.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40 text-gray-600 dark:text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>

                  {/* <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/leads/${lead.id}`);
                    }}
                    className="p-2.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-950/40 text-gray-600 dark:text-gray-400 hover:text-orange-600 transition-colors opacity-70 group-hover:opacity-100"
                    title="Open"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button> */}
                </div>
              </div>

              {expandedLeads.has(lead.id) && (
                <div className="px-5 pb-5 pt-1 bg-gray-50 dark:bg-gray-950/60 border-t border-gray-200 dark:border-gray-800 text-sm text-gray-700 dark:text-gray-300">
                  <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">Description</p>
                  <p className="mb-4">
                    {lead.product_service || lead.notes || "No description yet..."}
                  </p>

                  <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">Latest Updates</p>
                  {activities[lead.id] ? (
                    activities[lead.id].length > 0 ? (
                      <ul className="space-y-2.5">
                        {activities[lead.id].map((act, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <span className="text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">
                              {new Date(act.date).toLocaleString()}
                            </span>
                            <span className="text-gray-700 dark:text-gray-300">{act.note}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400">No updates yet...</p>
                    )
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">Loading updates...</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ──── Log Call Modal ──────────────────────────────────────────────── */}
      {showLogCallModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <PhoneIcon className="w-7 h-7 text-orange-500" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Log New Call</h3>
              </div>
              <button onClick={closeLogCallModal} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <XMarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleCallSubmit} className="p-6 lg:p-8 space-y-8">
              {/* ... existing call form content ... */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Call Status</label>
                  <select value={callForm.status} onChange={(e) => setCallForm({ ...callForm, status: e.target.value })} className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm">
                    {["Connected", "RNR", "Busy", "Wrong Number", "Not Interested", "Follow-up Required"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Next Follow-up</label>
                  <input type="datetime-local" value={callForm.next_followup} onChange={(e) => setCallForm({ ...callForm, next_followup: e.target.value })} className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm" />
                </div>
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Notes</label>
                <textarea rows={5} value={callForm.notes} onChange={(e) => setCallForm({ ...callForm, notes: e.target.value })} className="w-full px-5 py-5 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm resize-none" placeholder="Call summary, customer response, objections, next steps..." />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-800">
                <button type="button" onClick={closeLogCallModal} className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-2xl transition-all text-base">Cancel</button>
                <button type="submit" className="flex-1 px-8 py-4 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-base">Save Call Log</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ──── Schedule Site Visit Modal ──────────────────────────────────── */}
      {showSiteVisitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-7 h-7 text-orange-500" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Schedule Meeting</h3>
              </div>
              <button onClick={closeSiteVisitModal} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <XMarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSiteVisitSubmit} className="p-6 lg:p-8 space-y-8">
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Scheduled Date *</label>
                <input type="date" value={siteVisitForm.scheduled_date} onChange={(e) => setSiteVisitForm({ ...siteVisitForm, scheduled_date: e.target.value })} className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm" required />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Notes</label>
                <textarea rows={4} value={siteVisitForm.notes} onChange={(e) => setSiteVisitForm({ ...siteVisitForm, notes: e.target.value })} className="w-full px-5 py-5 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm resize-none" placeholder="Location details, special instructions, or notes..." />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-800">
                <button type="button" onClick={closeSiteVisitModal} className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-2xl transition-all text-base">Cancel</button>
                <button type="submit" className="flex-1 px-8 py-4 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-base">Schedule Visit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ──── Create Task Modal ───────────────────────────────────────────── */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <PlusIcon className="w-7 h-7 text-orange-500" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Create Task</h3>
              </div>
              <button onClick={closeCreateTaskModal} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <XMarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleTaskSubmit} className="p-6 lg:p-8 space-y-8">
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Task Title *</label>
                <input type="text" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm" placeholder="e.g., Follow up with client" required />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Description</label>
                <textarea rows={4} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} className="w-full px-5 py-5 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm resize-none" placeholder="Details about the task..." />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Due Date</label>
                <input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-base shadow-sm" />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-800">
                <button type="button" onClick={closeCreateTaskModal} className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-2xl transition-all text-base">Cancel</button>
                <button type="submit" className="flex-1 px-8 py-4 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-base">Create Task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ──── View Follow-ups & Visits Modal (NEW) ──────────────────────── */}
      {showFollowupsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <ChatBubbleLeftIcon className="w-7 h-7 text-orange-500" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Follow-ups & Meeting</h3>
              </div>
              <button onClick={closeFollowupsModal} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <XMarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 lg:p-8 overflow-y-auto flex-1 space-y-8">
              {followupsLoading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-orange-500"></div>
                </div>
              ) : (
                <>
                  {/* Follow-ups Section */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <ClockIcon className="w-5 h-5 text-purple-500" />
                      Follow-ups
                    </h4>
                    {followupsData.length > 0 ? (
                      <div className="space-y-4">
                        {followupsData.map((f, idx) => (
                          <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                            <div className="flex items-start gap-3">
                              <ChatBubbleLeftIcon className="w-6 h-6 text-purple-500 mt-1" />
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {f.notes || "Follow-up scheduled"}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                  {new Date(f.scheduled_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-6">No follow-ups scheduled yet.</p>
                    )}
                  </div>

                  {/* Site Visits Section */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-amber-500" />
                      Meeting
                    </h4>
                    {siteVisitsData.length > 0 ? (
                      <div className="space-y-4">
                        {siteVisitsData.map((v, idx) => (
                          <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                            <div className="flex items-start gap-3">
                              <CalendarIcon className="w-6 h-6 text-amber-500 mt-1" />
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  Site Visit - {v.status || "Scheduled"}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                  {new Date(v.scheduled_date).toLocaleString()}
                                </p>
                                {v.notes && <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{v.notes}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-6">No Meeting  scheduled yet.</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end">
              <button
                onClick={closeFollowupsModal}
                className="px-8 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-2xl transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ← Add Edit Lead Drawer here – after all other modals */}
      <EditLeadDrawer
        open={!!editLeadId}
        onClose={() => setEditLeadId(null)}
        leadId={editLeadId}
        onLeadUpdated={() => {
          fetchLeads();
          setEditLeadId(null);
        }}
      />
      
    </div>
  );
}
